// Shared capacity-based inventory math for Tier II.
// Used by both the questionnaire's save-time calculation and Threshold Review so the
// number a user previews always matches the value that gets persisted.
import { usageAmountToPounds } from '../../tri-shared/services/formulas/units.ts';
import type { CapacityLine } from '../../tri-shared/types/db.ts';

/** Pounds on hand for a vessel count. Null means unconvertible, not zero inventory. */
export function capacityLineToLbs(
  line: CapacityLine,
  count: number | null,
  specificGravity: number | null,
): number | null {
  if (line.capacityAmount == null || count == null) return null;
  const result = usageAmountToPounds(line.capacityAmount * count, line.capacityUnit, specificGravity);
  return typeof result === 'number' ? result : null;
}

/**
 * How one line's average was derived, alongside the pounds it contributes.
 *
 * `missing` is the case worth naming: once any line on the record states an average, a line that
 * states none leaves the average total unknown. Substituting its maximum would report a number the
 * user never entered, so callers that show their work must flag the line as incomplete instead.
 */
export type CapacityLineAvg =
  | { basis: 'percent'; percent: number; lbs: number | null }
  | { basis: 'count'; count: number; lbs: number | null }
  | { basis: 'missing'; lbs: null };

/** True when any line states an average, which is what makes an average total meaningful at all. */
export function capacityLinesHaveAvg(lines: CapacityLine[]): boolean {
  return lines.some((line) => line.avgCount != null || line.avgMode === 'percent');
}

/**
 * The single definition of a line's average contribution. Both the total below and the
 * questionnaire's per-vessel breakdown derive from this, so the rows always sum to the total.
 */
export function capacityLineAvg(line: CapacityLine, specificGravity: number | null): CapacityLineAvg {
  if (line.avgMode === 'percent') {
    if (line.avgPercent == null) return { basis: 'missing', lbs: null };
    const percent = line.avgPercent;
    const maxLbs = capacityLineToLbs(line, line.maxCount, specificGravity);
    return { basis: 'percent', percent, lbs: maxLbs == null ? null : maxLbs * (percent / 100) };
  }
  if (line.avgCount != null) {
    return { basis: 'count', count: line.avgCount, lbs: capacityLineToLbs(line, line.avgCount, specificGravity) };
  }
  return { basis: 'missing', lbs: null };
}

/**
 * Max and average pounds on hand across all vessel lines.
 * `avgAmountLbs` is null when no line provides average data (count or percent), or when any line
 * is missing its average. Either total is also null if any contributing line cannot be converted.
 */
export function capacityLinesToLbs(
  lines: CapacityLine[],
  specificGravity: number | null,
): { maxAmountLbs: number | null; avgAmountLbs: number | null } {
  // Never sum only the convertible rows: a partial total would understate inventory.
  const sumKnown = (values: (number | null)[]): number | null =>
    values.reduce<number | null>((sum, value) => (sum == null || value == null ? null : sum + value), 0);
  const maxAmountLbs = sumKnown(lines.map((line) => capacityLineToLbs(line, line.maxCount, specificGravity)));
  const avgAmountLbs = capacityLinesHaveAvg(lines)
    ? sumKnown(lines.map((line) => capacityLineAvg(line, specificGravity).lbs))
    : null;
  return { maxAmountLbs, avgAmountLbs };
}
